/**
 * Color palette configurations for project themes
 */

export interface ColorPalette {
  id: string;
  name: string;
  description: string;
  colors: {
    primary: string;
    primaryDark: string;
    secondary: string;
    secondaryDark: string;
    accent: string;
    accentDark: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    error: string;
    warning: string;
    success: string;
    info: string;
  };
  preview?: string[]; // Array of main colors for preview
  category: "modern" | "retro" | "professional" | "playful" | "dark" | "light";
}

export const colorPalettes: ColorPalette[] = [
  {
    id: "shadcn",
    name: "Minimal Pro",
    description: "Clean, modern design system",
    category: "modern",
    colors: {
      primary: "#1A1A1A",
      primaryDark: "#0A0A0A",
      secondary: "#F5F5F5",
      secondaryDark: "#E0E0E0",
      accent: "#2563EB",
      accentDark: "#1D4ED8",
      background: "#FFFFFF",
      surface: "#FAFAFA",
      text: "#1A1A1A",
      textSecondary: "#6B7280",
      error: "#DC2626",
      warning: "#F59E0B",
      success: "#16A34A",
      info: "#2563EB",
    },
    preview: ["#1A1A1A", "#2563EB", "#16A34A", "#F5F5F5"],
  },
  {
    id: "vercel",
    name: "Monochrome",
    description: "High contrast black and white theme",
    category: "modern",
    colors: {
      primary: "#000000",
      primaryDark: "#111111",
      secondary: "#FFFFFF",
      secondaryDark: "#FAFAFA",
      accent: "#0070F3",
      accentDark: "#0051CC",
      background: "#FFFFFF",
      surface: "#FAFAFA",
      text: "#000000",
      textSecondary: "#666666",
      error: "#EE0000",
      warning: "#F5A623",
      success: "#50E3C2",
      info: "#0070F3",
    },
    preview: ["#000000", "#0070F3", "#50E3C2", "#FAFAFA"],
  },
  {
    id: "stripe",
    name: "Gradient",
    description: "Vibrant gradient-friendly palette",
    category: "modern",
    colors: {
      primary: "#5E3FBE",
      primaryDark: "#4B32A3",
      secondary: "#00BBDD",
      secondaryDark: "#0099BB",
      accent: "#00C853",
      accentDark: "#00A041",
      background: "#FFFFFF",
      surface: "#F7F9FC",
      text: "#1A2332",
      textSecondary: "#5A6169",
      error: "#E53935",
      warning: "#FB8C00",
      success: "#00C853",
      info: "#00BBDD",
    },
    preview: ["#5E3FBE", "#00BBDD", "#00C853", "#1A2332"],
  },
  {
    id: "linear",
    name: "Midnight",
    description: "Dark productive workspace theme",
    category: "dark",
    colors: {
      primary: "#5E6AD2",
      primaryDark: "#4B57C3",
      secondary: "#F7F8F8",
      secondaryDark: "#E8E8E8",
      accent: "#5E6AD2",
      accentDark: "#4B57C3",
      background: "#0E0E10",
      surface: "#1A1523",
      text: "#F7F8F8",
      textSecondary: "#8F8F8F",
      error: "#F04438",
      warning: "#F79009",
      success: "#12B76A",
      info: "#5E6AD2",
    },
    preview: ["#5E6AD2", "#F7F8F8", "#12B76A", "#1A1523"],
  },
  {
    id: "radix",
    name: "Accessible",
    description: "High contrast accessible colors",
    category: "modern",
    colors: {
      primary: "#4E4BEC",
      primaryDark: "#3730C3",
      secondary: "#E5E5E5",
      secondaryDark: "#D4D4D4",
      accent: "#4E4BEC",
      accentDark: "#3730C3",
      background: "#FFFFFF",
      surface: "#FCFCFD",
      text: "#11181C",
      textSecondary: "#687076",
      error: "#E5484D",
      warning: "#FFB224",
      success: "#46A758",
      info: "#0091FF",
    },
    preview: ["#4E4BEC", "#0091FF", "#46A758", "#11181C"],
  },
  {
    id: "tailwind",
    name: "Breeze",
    description: "Fresh and balanced colors",
    category: "modern",
    colors: {
      primary: "#0EA5E9",
      primaryDark: "#0284C7",
      secondary: "#8B5CF6",
      secondaryDark: "#7C3AED",
      accent: "#10B981",
      accentDark: "#059669",
      background: "#FFFFFF",
      surface: "#F9FAFB",
      text: "#111827",
      textSecondary: "#6B7280",
      error: "#EF4444",
      warning: "#F59E0B",
      success: "#10B981",
      info: "#0EA5E9",
    },
    preview: ["#0EA5E9", "#8B5CF6", "#10B981", "#F59E0B"],
  },
  {
    id: "notion",
    name: "Canvas",
    description: "Minimalist workspace theme",
    category: "light",
    colors: {
      primary: "#000000",
      primaryDark: "#191919",
      secondary: "#37352F",
      secondaryDark: "#2F2F2F",
      accent: "#2383E2",
      accentDark: "#0C7CC7",
      background: "#FFFFFF",
      surface: "#F7F6F3",
      text: "#37352F",
      textSecondary: "#787774",
      error: "#EB5757",
      warning: "#FFC400",
      success: "#0DBF69",
      info: "#2383E2",
    },
    preview: ["#000000", "#2383E2", "#0DBF69", "#F7F6F3"],
  },
  {
    id: "nextjs",
    name: "Contrast",
    description: "Sharp black and blue accents",
    category: "modern",
    colors: {
      primary: "#000000",
      primaryDark: "#111111",
      secondary: "#FFFFFF",
      secondaryDark: "#FAFAFA",
      accent: "#0070F3",
      accentDark: "#0051CC",
      background: "#FFFFFF",
      surface: "#FAFAFA",
      text: "#000000",
      textSecondary: "#666666",
      error: "#EE0000",
      warning: "#F5A623",
      success: "#50E3C2",
      info: "#0070F3",
    },
    preview: ["#000000", "#0070F3", "#50E3C2", "#FFFFFF"],
  },
  {
    id: "chakra",
    name: "Vibrant",
    description: "Bold and energetic colors",
    category: "modern",
    colors: {
      primary: "#319795",
      primaryDark: "#2C7A7B",
      secondary: "#805AD5",
      secondaryDark: "#6B46C1",
      accent: "#ED8936",
      accentDark: "#DD6B20",
      background: "#FFFFFF",
      surface: "#F7FAFC",
      text: "#1A202C",
      textSecondary: "#718096",
      error: "#F56565",
      warning: "#ED8936",
      success: "#48BB78",
      info: "#4299E1",
    },
    preview: ["#319795", "#805AD5", "#ED8936", "#48BB78"],
  },
  {
    id: "default",
    name: "Classic",
    description: "Balanced traditional scheme",
    category: "modern",
    colors: {
      primary: "#3B82F6",
      primaryDark: "#1E40AF",
      secondary: "#8B5CF6",
      secondaryDark: "#6D28D9",
      accent: "#F59E0B",
      accentDark: "#D97706",
      background: "#FFFFFF",
      surface: "#F9FAFB",
      text: "#111827",
      textSecondary: "#6B7280",
      error: "#EF4444",
      warning: "#F59E0B",
      success: "#10B981",
      info: "#3B82F6",
    },
    preview: ["#3B82F6", "#8B5CF6", "#F59E0B", "#10B981"],
  },
  {
    id: "neon",
    name: "Electric",
    description: "Vibrant neon colors for bold designs",
    category: "playful",
    colors: {
      primary: "#FF0266",
      primaryDark: "#CC0052",
      secondary: "#00D4FF",
      secondaryDark: "#00A8CC",
      accent: "#FFDE00",
      accentDark: "#FFB300",
      background: "#0F0F0F",
      surface: "#1A1A1A",
      text: "#F0F0F0",
      textSecondary: "#B8B8B8",
      error: "#FF3333",
      warning: "#FFAA00",
      success: "#00FF88",
      info: "#00AAFF",
    },
    preview: ["#FF0266", "#00D4FF", "#FFDE00", "#00FF88"],
  },
  {
    id: "ocean",
    name: "Aqua",
    description: "Calm blues and aqua tones",
    category: "professional",
    colors: {
      primary: "#0891B2",
      primaryDark: "#0E7490",
      secondary: "#06B6D4",
      secondaryDark: "#0891B2",
      accent: "#14B8A6",
      accentDark: "#0D9488",
      background: "#F0FDFA",
      surface: "#CCFBF1",
      text: "#134E4A",
      textSecondary: "#14B8A6",
      error: "#F87171",
      warning: "#FCD34D",
      success: "#34D399",
      info: "#60A5FA",
    },
    preview: ["#0891B2", "#06B6D4", "#14B8A6", "#34D399"],
  },
  {
    id: "sunset",
    name: "Sunset",
    description: "Warm oranges and purples",
    category: "playful",
    colors: {
      primary: "#F97316",
      primaryDark: "#EA580C",
      secondary: "#A855F7",
      secondaryDark: "#9333EA",
      accent: "#EAB308",
      accentDark: "#CA8A04",
      background: "#FFF7ED",
      surface: "#FED7AA",
      text: "#431407",
      textSecondary: "#92400E",
      error: "#DC2626",
      warning: "#F59E0B",
      success: "#16A34A",
      info: "#2563EB",
    },
    preview: ["#F97316", "#A855F7", "#EAB308", "#EC4899"],
  },
  {
    id: "forest",
    name: "Nature",
    description: "Natural greens and earth tones",
    category: "professional",
    colors: {
      primary: "#16A34A",
      primaryDark: "#15803D",
      secondary: "#84CC16",
      secondaryDark: "#65A30D",
      accent: "#A16207",
      accentDark: "#854D0E",
      background: "#F7FEE7",
      surface: "#ECFCCB",
      text: "#1A2E05",
      textSecondary: "#365314",
      error: "#DC2626",
      warning: "#F59E0B",
      success: "#16A34A",
      info: "#0EA5E9",
    },
    preview: ["#16A34A", "#84CC16", "#A16207", "#0EA5E9"],
  },
  {
    id: "monochrome",
    name: "Grayscale",
    description: "Classic black and white with gray accents",
    category: "professional",
    colors: {
      primary: "#000000",
      primaryDark: "#171717",
      secondary: "#525252",
      secondaryDark: "#404040",
      accent: "#737373",
      accentDark: "#525252",
      background: "#FFFFFF",
      surface: "#F5F5F5",
      text: "#000000",
      textSecondary: "#525252",
      error: "#991B1B",
      warning: "#92400E",
      success: "#14532D",
      info: "#1E3A8A",
    },
    preview: ["#000000", "#525252", "#737373", "#F5F5F5"],
  },
  {
    id: "cyberpunk",
    name: "Cyber",
    description: "High-tech meets low-life aesthetics",
    category: "dark",
    colors: {
      primary: "#FF00FF",
      primaryDark: "#CC00CC",
      secondary: "#00FFFF",
      secondaryDark: "#00CCCC",
      accent: "#FFFF00",
      accentDark: "#CCCC00",
      background: "#0A0A0A",
      surface: "#1A0A1A",
      text: "#FFFFFF",
      textSecondary: "#FF00FF",
      error: "#FF0040",
      warning: "#FFA500",
      success: "#00FF88",
      info: "#00B4FF",
    },
    preview: ["#FF00FF", "#00FFFF", "#FFFF00", "#00FF88"],
  },
  {
    id: "pastel",
    name: "Soft",
    description: "Soft, gentle pastel colors",
    category: "light",
    colors: {
      primary: "#C084FC",
      primaryDark: "#A855F7",
      secondary: "#F9A8D4",
      secondaryDark: "#F472B6",
      accent: "#FDE047",
      accentDark: "#FACC15",
      background: "#FDF4FF",
      surface: "#FAE8FF",
      text: "#581C87",
      textSecondary: "#9333EA",
      error: "#FCA5A5",
      warning: "#FDE68A",
      success: "#BBF7D0",
      info: "#BFDBFE",
    },
    preview: ["#C084FC", "#F9A8D4", "#FDE047", "#BBF7D0"],
  },
  {
    id: "retro",
    name: "Retro",
    description: "80s inspired retro colors",
    category: "retro",
    colors: {
      primary: "#FF6B6B",
      primaryDark: "#EE5A6F",
      secondary: "#4ECDC4",
      secondaryDark: "#45B7AA",
      accent: "#FFE66D",
      accentDark: "#F7D060",
      background: "#2B2D42",
      surface: "#3D3F58",
      text: "#F7FFF7",
      textSecondary: "#8D99AE",
      error: "#FF4444",
      warning: "#FFB700",
      success: "#44FF88",
      info: "#44DDFF",
    },
    preview: ["#FF6B6B", "#4ECDC4", "#FFE66D", "#44FF88"],
  },
  {
    id: "corporate",
    name: "Business",
    description: "Professional business colors",
    category: "professional",
    colors: {
      primary: "#1E3A8A",
      primaryDark: "#1E2F5C",
      secondary: "#4338CA",
      secondaryDark: "#3730A3",
      accent: "#0891B2",
      accentDark: "#0E7490",
      background: "#F8FAFC",
      surface: "#F1F5F9",
      text: "#0F172A",
      textSecondary: "#475569",
      error: "#DC2626",
      warning: "#F59E0B",
      success: "#059669",
      info: "#0891B2",
    },
    preview: ["#1E3A8A", "#4338CA", "#0891B2", "#059669"],
  },
  {
    id: "dracula",
    name: "Vampire",
    description: "Popular dark theme inspired by Dracula",
    category: "dark",
    colors: {
      primary: "#BD93F9",
      primaryDark: "#9B72F2",
      secondary: "#FF79C6",
      secondaryDark: "#FF5CAA",
      accent: "#F1FA8C",
      accentDark: "#E7F06F",
      background: "#282A36",
      surface: "#44475A",
      text: "#F8F8F2",
      textSecondary: "#6272A4",
      error: "#FF5555",
      warning: "#FFB86C",
      success: "#50FA7B",
      info: "#8BE9FD",
    },
    preview: ["#BD93F9", "#FF79C6", "#F1FA8C", "#50FA7B"],
  },
  {
    id: "material",
    name: "Material",
    description: "Google's Material Design colors",
    category: "modern",
    colors: {
      primary: "#1E88E5",
      primaryDark: "#1565C0",
      secondary: "#EC407A",
      secondaryDark: "#C2185B",
      accent: "#7E57C2",
      accentDark: "#5E35B1",
      background: "#FAFAFA",
      surface: "#FFFFFF",
      text: "#212121",
      textSecondary: "#757575",
      error: "#E53935",
      warning: "#FB8C00",
      success: "#43A047",
      info: "#1E88E5",
    },
    preview: ["#1E88E5", "#EC407A", "#7E57C2", "#43A047"],
  },
  {
    id: "github",
    name: "Developer",
    description: "GitHub's signature dark theme",
    category: "dark",
    colors: {
      primary: "#58A6FF",
      primaryDark: "#1F6FEB",
      secondary: "#F78166",
      secondaryDark: "#DA3633",
      accent: "#A371F7",
      accentDark: "#8957E5",
      background: "#0D1117",
      surface: "#161B22",
      text: "#C9D1D9",
      textSecondary: "#8B949E",
      error: "#F85149",
      warning: "#D29922",
      success: "#56D364",
      info: "#58A6FF",
    },
    preview: ["#58A6FF", "#F78166", "#A371F7", "#56D364"],
  },
  {
    id: "solarized",
    name: "Solar",
    description: "Precision colors for machines and people",
    category: "professional",
    colors: {
      primary: "#268BD2",
      primaryDark: "#073642",
      secondary: "#2AA198",
      secondaryDark: "#00736F",
      accent: "#B58900",
      accentDark: "#946C00",
      background: "#FDF6E3",
      surface: "#EEE8D5",
      text: "#073642",
      textSecondary: "#586E75",
      error: "#DC322F",
      warning: "#CB4B16",
      success: "#859900",
      info: "#268BD2",
    },
    preview: ["#268BD2", "#2AA198", "#B58900", "#859900"],
  },
  {
    id: "nord",
    name: "Arctic",
    description: "Arctic, north-bluish color palette",
    category: "professional",
    colors: {
      primary: "#5E81AC",
      primaryDark: "#2E3440",
      secondary: "#81A1C1",
      secondaryDark: "#4C566A",
      accent: "#88C0D0",
      accentDark: "#5E81AC",
      background: "#ECEFF4",
      surface: "#E5E9F0",
      text: "#2E3440",
      textSecondary: "#4C566A",
      error: "#BF616A",
      warning: "#D08770",
      success: "#A3BE8C",
      info: "#5E81AC",
    },
    preview: ["#5E81AC", "#81A1C1", "#88C0D0", "#A3BE8C"],
  },
  {
    id: "monokai",
    name: "Neon Code",
    description: "Vibrant colors on dark background",
    category: "dark",
    colors: {
      primary: "#66D9EF",
      primaryDark: "#49B8CC",
      secondary: "#F92672",
      secondaryDark: "#D6004F",
      accent: "#A6E22E",
      accentDark: "#83B00B",
      background: "#272822",
      surface: "#3E3D32",
      text: "#F8F8F2",
      textSecondary: "#75715E",
      error: "#F92672",
      warning: "#FD971F",
      success: "#A6E22E",
      info: "#66D9EF",
    },
    preview: ["#66D9EF", "#F92672", "#A6E22E", "#FD971F"],
  },
  {
    id: "gruvbox",
    name: "Earthy",
    description: "Retro groove color scheme",
    category: "retro",
    colors: {
      primary: "#458588",
      primaryDark: "#076678",
      secondary: "#CC241D",
      secondaryDark: "#9D0006",
      accent: "#D79921",
      accentDark: "#B57614",
      background: "#FBF1C7",
      surface: "#EBDBB2",
      text: "#282828",
      textSecondary: "#504945",
      error: "#CC241D",
      warning: "#D65D0E",
      success: "#98971A",
      info: "#458588",
    },
    preview: ["#458588", "#CC241D", "#D79921", "#98971A"],
  },
  {
    id: "tokyo-night",
    name: "Tokyo",
    description: "A clean dark theme inspired by Tokyo nights",
    category: "dark",
    colors: {
      primary: "#7AA2F7",
      primaryDark: "#3D59A1",
      secondary: "#BB9AF7",
      secondaryDark: "#9D7CD8",
      accent: "#7DCFFF",
      accentDark: "#4FD1C5",
      background: "#1A1B26",
      surface: "#24283B",
      text: "#C0CAF5",
      textSecondary: "#9699A8",
      error: "#F7768E",
      warning: "#E0AF68",
      success: "#9ECE6A",
      info: "#7AA2F7",
    },
    preview: ["#7AA2F7", "#BB9AF7", "#7DCFFF", "#9ECE6A"],
  },
  {
    id: "catppuccin",
    name: "Latte",
    description: "Soothing pastel theme for the high-spirited",
    category: "light",
    colors: {
      primary: "#8839EF",
      primaryDark: "#7C3AED",
      secondary: "#EA76CB",
      secondaryDark: "#E54D9E",
      accent: "#FE640B",
      accentDark: "#EF4F00",
      background: "#EFF1F5",
      surface: "#DCE0E8",
      text: "#4C4F69",
      textSecondary: "#6C6F85",
      error: "#D20F39",
      warning: "#DF8E1D",
      success: "#40A02B",
      info: "#1E66F5",
    },
    preview: ["#8839EF", "#EA76CB", "#FE640B", "#40A02B"],
  },
  {
    id: "one-dark",
    name: "Shadow",
    description: "Atom's iconic One Dark theme",
    category: "dark",
    colors: {
      primary: "#61AFEF",
      primaryDark: "#4B8BBE",
      secondary: "#C678DD",
      secondaryDark: "#A454BA",
      accent: "#E06C75",
      accentDark: "#BE5046",
      background: "#282C34",
      surface: "#2C313A",
      text: "#ABB2BF",
      textSecondary: "#5C6370",
      error: "#E06C75",
      warning: "#E5C07B",
      success: "#98C379",
      info: "#61AFEF",
    },
    preview: ["#61AFEF", "#C678DD", "#E06C75", "#98C379"],
  },
  {
    id: "ayu",
    name: "Bright",
    description: "A simple theme with bright colors",
    category: "modern",
    colors: {
      primary: "#36A3D9",
      primaryDark: "#0A7ACA",
      secondary: "#F07178",
      secondaryDark: "#E7484F",
      accent: "#FFB454",
      accentDark: "#FF8B2B",
      background: "#FAFAFA",
      surface: "#F3F4F5",
      text: "#575F66",
      textSecondary: "#8A9199",
      error: "#F07178",
      warning: "#FFB454",
      success: "#95C670",
      info: "#36A3D9",
    },
    preview: ["#36A3D9", "#F07178", "#FFB454", "#95C670"],
  },
  {
    id: "synthwave",
    name: "Synthwave",
    description: "Neon synthwave aesthetic",
    category: "playful",
    colors: {
      primary: "#FF7EDB",
      primaryDark: "#FF4EC2",
      secondary: "#72F1B8",
      secondaryDark: "#42C18F",
      accent: "#FEDE5D",
      accentDark: "#FECE2A",
      background: "#2A2139",
      surface: "#34294F",
      text: "#FFFFFF",
      textSecondary: "#B097F9",
      error: "#FE4450",
      warning: "#FF8B39",
      success: "#72F1B8",
      info: "#03EDF9",
    },
    preview: ["#FF7EDB", "#72F1B8", "#FEDE5D", "#03EDF9"],
  },
  {
    id: "custom",
    name: "Custom",
    description: "Create your own color palette",
    category: "modern",
    colors: {
      primary: "#3B82F6",
      primaryDark: "#1E40AF",
      secondary: "#8B5CF6",
      secondaryDark: "#6D28D9",
      accent: "#F59E0B",
      accentDark: "#D97706",
      background: "#FFFFFF",
      surface: "#F9FAFB",
      text: "#111827",
      textSecondary: "#6B7280",
      error: "#EF4444",
      warning: "#F59E0B",
      success: "#10B981",
      info: "#3B82F6",
    },
    preview: ["#3B82F6", "#8B5CF6", "#F59E0B", "#10B981"],
  },
];

/**
 * Get a color palette by its ID
 * @param id - The unique identifier of the color palette
 * @returns The color palette object or undefined if not found
 */
export const getColorPaletteById = (id: string): ColorPalette | undefined => {
  return colorPalettes.find((palette) => palette.id === id);
};

/**
 * Get all color palettes in a specific category
 * @param category - The category to filter by
 * @returns Array of color palettes in the specified category
 */
export const getColorPalettesByCategory = (category: ColorPalette["category"]): ColorPalette[] => {
  return colorPalettes.filter((palette) => palette.category === category);
};
